/**
 * B4 · G3 — "Prepay and save" card.
 *
 * Owner's business rule: the student took and paid for the first month, and
 * is then offered paying the remaining months in advance with a discount
 * percentage. The declared reason is cash flow.
 *
 * Three product decisions that explain the design:
 *
 *  1. **The savings are shown in USD AND in VES.** The commitment is in USD
 *     (it doesn't move if the BCV rate changes tomorrow), but the student
 *     thinks in `bolívares`: a card that only says "$22.50" doesn't convince
 *     anyone in Venezuela. The VES line carries the mark of being
 *     indicative, not a promise.
 *
 *  2. **When there's NO offer, the card explains what's missing** instead
 *     of disappearing. "Pay your first installment and unlock 10%" is the
 *     business's own condition turned into an incentive; hiding the card
 *     wastes the exact moment the student is looking at their debt.
 *
 *  3. **The amount the student had in front of them is what's sent.** If
 *     someone credits an installment between them seeing the offer and
 *     clicking, the server rejects instead of charging an amount different
 *     from what they approved.
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { PiggyBank, Sparkles } from "@fleet/icons";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@fleet/ui";
import { showToast } from "@/components/ui/toast";
import { useFeatureEnabled } from "@/features/flags/use-feature-enabled";
import { Money } from "./money";
import { type PrepayOffer, useAcceptPrepay, useMePrepayOffers } from "../api";

const BP_PER_PERCENT = 100;

/** `1050` → `"10.5"`. Formatted with the user's locale. */
const percentLabel = (percentBp: number, locale: string): string =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(percentBp / BP_PER_PERCENT);

export const PrepayCard: Component<{ vesPerUsd?: number | null }> = (props) => {
  const { t, locale } = useI18n();
  // The offer is an academy feature: if it's off, it isn't even requested.
  const enabled = useFeatureEnabled(() => "billing.prepay");
  const offers = useMePrepayOffers();
  const accept = useAcceptPrepay();
  const [pending, setPending] = createSignal<string | null>(null);

  // Only the best live offer matters; the rest of the enrollments are
  // listed as "not yet" with their reason, without competing for attention.
  const eligible = createMemo(() =>
    (offers.data ?? []).filter((o) => o.eligibility === "eligible" && o.quote),
  );
  const blocked = createMemo(() => (offers.data ?? []).filter((o) => o.eligibility !== "eligible"));

  const reasonLabel = (o: PrepayOffer): string => {
    switch (o.eligibility) {
      case "first_installment_unpaid": {
        return t(msg`Pay your first instalment and this discount unlocks.`);
      }
      case "not_enough_installments": {
        return t(msg`There aren't enough upcoming instalments left to bring forward.`);
      }
      case "already_prepaid": {
        return t(msg`You already brought this enrolment forward. Nothing else is due.`);
      }
      default: {
        return "";
      }
    }
  };

  const onAccept = (o: PrepayOffer) => {
    const { quote } = o;
    if (!quote) {
      return;
    }
    setPending(o.enrollmentId);
    accept.mutate(
      {
        enrollmentId: o.enrollmentId,
        // Anti-surprise: the server compares against the live offer and
        // rejects if it changed while the student was deciding.
        expectedTotalUsdMinor: quote.totalUsdMinor,
        expectedDiscountUsdMinor: quote.discountUsdMinor,
      },
      {
        onSuccess: () => {
          setPending(null);
          showToast(t(msg`Done: your remaining instalments are now a single invoice.`), "success");
        },
        onError: (e) => {
          setPending(null);
          showToast(e.message, "error");
        },
      },
    );
  };

  return (
    <Show when={enabled().enabled}>
      <Show when={!offers.isLoading} fallback={<Skeleton class="h-28 w-full" />}>
        <Show when={(offers.data?.length ?? 0) > 0}>
          <div class="space-y-3" data-testid="prepay-card">
            <For each={eligible()}>
              {(o) => (
                <div
                  class="rounded-xl border border-primary/40 bg-primary/5 p-4"
                  data-testid={`prepay-offer-${o.enrollmentId}`}
                >
                  <div class="flex items-center gap-2">
                    <Sparkles class="h-4 w-4 text-primary" />
                    <h3 class="font-semibold">{t(msg`Pay ahead and save`)}</h3>
                    <span
                      class="ml-auto rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground"
                      data-testid="prepay-percent"
                    >
                      −{percentLabel(o.quote?.percentBp ?? 0, locale())}%
                    </span>
                  </div>
                  <p class="mt-2 text-sm text-muted-foreground">
                    {(() => {
                      const count = o.quote?.installmentCount ?? 0;
                      return t(msg`Settle your ${count} remaining instalments in one payment.`);
                    })()}
                  </p>
                  <dl class="mt-3 space-y-1 text-sm">
                    <div class="flex items-center justify-between">
                      <dt class="text-muted-foreground">{t(msg`Month by month`)}</dt>
                      <dd class="line-through opacity-60">
                        <Money
                          usdMinor={o.quote?.baseUsdMinor ?? 0}
                          vesMinor={o.baseVesMinor}
                          showRate={false}
                        />
                      </dd>
                    </div>
                    <div class="flex items-center justify-between">
                      <dt class="font-medium">{t(msg`Paying ahead`)}</dt>
                      <dd data-testid="prepay-total">
                        <Money
                          usdMinor={o.quote?.totalUsdMinor ?? 0}
                          vesMinor={o.totalVesMinor}
                          showRate={false}
                        />
                      </dd>
                    </div>
                    <div class="flex items-center justify-between text-emerald-700 dark:text-emerald-400">
                      <dt class="flex items-center gap-1 font-semibold">
                        <PiggyBank class="h-4 w-4" />
                        {t(msg`You save`)}
                      </dt>
                      <dd class="font-semibold" data-testid="prepay-savings">
                        <Money
                          usdMinor={o.quote?.discountUsdMinor ?? 0}
                          vesMinor={o.savingsVesMinor}
                          showRate={false}
                        />
                      </dd>
                    </div>
                  </dl>
                  {/* The commitment is in USD: the conversion is for reference only. */}
                  <Show when={props.vesPerUsd}>
                    <p class="mt-2 text-xs text-muted-foreground">
                      {t(msg`The amount is fixed in USD; the bolívar figure follows today's rate.`)}
                    </p>
                  </Show>
                  <Button
                    class="mt-3 w-full"
                    data-testid={`prepay-accept-${o.enrollmentId}`}
                    disabled={pending() === o.enrollmentId}
                    onClick={() => {
                      onAccept(o);
                    }}
                  >
                    {t(msg`Bring my instalments forward`)}
                  </Button>
                </div>
              )}
            </For>

            {/* No offer yet: it says WHAT's missing, which is the hook itself. */}
            <For each={blocked()}>
              {(o) => (
                <div
                  class="rounded-xl border border-dashed border-border p-3 text-sm text-muted-foreground"
                  data-testid={`prepay-blocked-${o.enrollmentId}`}
                >
                  <span class="font-medium text-foreground">{t(msg`Pay ahead and save`)}</span>
                  <span> · {reasonLabel(o)}</span>
                </div>
              )}
            </For>
          </div>
        </Show>
      </Show>
    </Show>
  );
};
